import React from 'react'
import {
  CheckIcon,
  DownloadIcon,
  FileCodeIcon,
  FileTextIcon,
  Loader2Icon,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'

import { downloadMarkdownAsPDF } from '@/functions/downloadMarkdownAsPDF'
import { downloadMarkdownFile } from '@/lib/utils'

export const ResumeDownloader = (props: {
  resumeText: string
  filename?: string
  isStreaming?: boolean
}) => {
  const { isStreaming, resumeText } = props

  const [downloadFilename, setDownloadFilename] = React.useState(
    props.filename || 'tailored-resume',
  )
  const [isDownloading, setIsDownloading] = React.useState<boolean>(false)
  const [open, setOpen] = React.useState(false)

  const [selectedFormats, setSelectedFormats] = React.useState<Set<string>>(
    new Set(),
  )

  const downloadMarkdown = React.useCallback(
    (opts: { filename: string; text: string }) => {
      downloadMarkdownFile(opts)
    },
    [],
  )

  const downloadPDF = React.useCallback(
    async (opts: { filename: string; text: string }) => {
      const { text, filename } = opts
      const response = await downloadMarkdownAsPDF({ data: text })
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `${filename.length > 0 ? filename : 'new-tailored-resume'}.pdf`,
      )
      document.body.appendChild(link)
      link.click()

      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }, 200)
    },
    [],
  )

  const handleExecuteDownloads = React.useCallback(async () => {
    setIsDownloading(true)

    try {
      const text = resumeText || ''

      if (selectedFormats.has('pdf')) {
        await downloadPDF({ text, filename: downloadFilename })
      }

      if (selectedFormats.has('markdown')) {
        downloadMarkdown({ text, filename: downloadFilename })
      }

      setOpen(false)
      setSelectedFormats(new Set())
    } catch (error) {
      console.error('Bulk download failed:', error)
    } finally {
      setIsDownloading(false)
    }
  }, [downloadFilename, selectedFormats, resumeText])

  const toggleFormat = (format: string) => {
    const next = new Set(selectedFormats)
    next.has(format) ? next.delete(format) : next.add(format)
    setSelectedFormats(next)
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} disabled={isStreaming}>
      <div className="relative inline-flex h-9 flex-row">
        <Input
          className="pr-9"
          placeholder="Name for download files"
          disabled={isStreaming}
          value={downloadFilename}
          onChange={(e) => setDownloadFilename(e.target.value)}
        />
        <DropdownMenuTrigger
          render={(props) => (
            <Button
              {...props}
              variant="ghost"
              className="absolute right-0"
              disabled={isStreaming}
            >
              {isDownloading ? (
                <Loader2Icon className="h-4 w-4 animate-spin" />
              ) : (
                <DownloadIcon className="h-4 w-4" />
              )}
            </Button>
          )}
        />
      </div>

      <DropdownMenuContent className="w-56" align="end">
        <DropdownMenuItem
          closeOnClick={false}
          onClick={() => {
            toggleFormat('markdown')
          }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center">
            <FileCodeIcon className="mr-2 h-4 w-4" />
            <span>Markdown (.md)</span>
          </div>
          {selectedFormats.has('markdown') && <CheckIcon className="h-4 w-4" />}
        </DropdownMenuItem>

        <DropdownMenuItem
          closeOnClick={false}
          onClick={() => {
            toggleFormat('pdf')
          }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center">
            <FileTextIcon className="mr-2 h-4 w-4" />
            <span>PDF (.pdf)</span>
          </div>
          {selectedFormats.has('pdf') && <CheckIcon className="h-4 w-4" />}
        </DropdownMenuItem>

        <div className="p-2">
          <Button
            className="w-full"
            size="sm"
            disabled={selectedFormats.size === 0 || isDownloading}
            onClick={handleExecuteDownloads}
          >
            {isDownloading
              ? 'Generating...'
              : `Download (${selectedFormats.size})`}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
